import { Suspense, lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider, useAuth } from './auth/AuthProvider';
import { SignIn } from './auth/SignIn';
import { Layout } from './components/Layout';
import { Spinner } from './components/ui';

// Lazy-load route screens so recharts (Dashboard) and other heavy deps don't
// inflate the initial bundle.
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Review = lazy(() => import('./pages/Review').then((m) => ({ default: m.Review })));
const Records = lazy(() => import('./pages/Records').then((m) => ({ default: m.Records })));
const Runs = lazy(() => import('./pages/Runs').then((m) => ({ default: m.Runs })));
const Push = lazy(() => import('./pages/Push').then((m) => ({ default: m.Push })));
const DataPage = lazy(() => import('./pages/DataPage').then((m) => ({ default: m.DataPage })));
const DataRefresh = lazy(() => import('./pages/DataRefresh').then((m) => ({ default: m.DataRefresh })));
const Recheck = lazy(() => import('./pages/Recheck').then((m) => ({ default: m.Recheck })));
const Health = lazy(() => import('./pages/Health').then((m) => ({ default: m.Health })));
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);

function Gate() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="grid h-screen place-items-center bg-ink-950">
        <Spinner />
      </div>
    );
  }
  // Match the Firestore rules / callable gate: a verified email is required, so
  // an unverified account lands back on sign-in rather than a permission-denied
  // dead-end.
  if (!user || !user.emailVerified) return <SignIn />;
  return (
    <Layout>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/review" element={<Review />} />
          <Route path="/records" element={<Records />} />
          <Route path="/runs" element={<Runs />} />
          <Route path="/push" element={<Push />} />
          <Route path="/data" element={<DataPage />} />
          <Route path="/data-refresh" element={<DataRefresh />} />
          <Route path="/recheck" element={<Recheck />} />
          <Route path="/health" element={<Health />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Gate />
      </BrowserRouter>
    </AuthProvider>
  );
}
